import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { fetchBenefitTypes, createBenefitType, updateBenefitType } from '../../../../../services/salary';
import CustomPagination from '../../../../../components/common/CustomPagination.js';

const BenefitTypes = () => {
  const { t } = useTranslation(['common', 'employees', 'salary']);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [benefitTypes, setBenefitTypes] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    is_active: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    code: '',
    category: 'other',
    is_taxable: false,
    is_employer_paid: true,
    is_employee_paid: false,
    default_amount: '',
    description: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        page: pagination.page + 1,
        page_size: pagination.rowsPerPage
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await fetchBenefitTypes(params);
      
      if (response.results) {
        setBenefitTypes(response.results);
        setPagination(prev => ({
          ...prev,
          totalCount: response.count || 0
        }));
      } else {
        setBenefitTypes(Array.isArray(response) ? response : []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch benefit types');
      setBenefitTypes([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPagination(prev => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  };

  const handleCustomRowsInputChange = (value) => {
    setCustomRowsInput(value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput);
    if (value && value > 0 && value <= 1000) {
      setPagination(prev => ({ ...prev, rowsPerPage: value, page: 0 }));
      setCustomRowsActive(false);
      setCustomRowsInput('');
    } else {
      setCustomRowsInput('');
      setCustomRowsActive(false);
    }
  };

  const handleOpenModal = (type = null) => {
    if (type) {
      setEditId(type.id);
      setForm({
        name: type.name || '',
        code: type.code || '',
        category: type.category || 'other',
        is_taxable: type.is_taxable !== undefined ? type.is_taxable : false,
        is_employer_paid: type.is_employer_paid !== undefined ? type.is_employer_paid : true,
        is_employee_paid: type.is_employee_paid !== undefined ? type.is_employee_paid : false,
        default_amount: type.default_amount || '',
        description: type.description || '',
        is_active: type.is_active !== undefined ? type.is_active : true
      });
    } else {
      setEditId(null);
      setForm({
        name: '',
        code: '',
        category: 'other',
        is_taxable: false,
        is_employer_paid: true,
        is_employee_paid: false,
        default_amount: '',
        description: '',
        is_active: true
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditId(null);
    setForm({
      name: '',
      code: '',
      category: 'other',
      is_taxable: false,
      is_employer_paid: true,
      is_employee_paid: false,
      default_amount: '',
      description: '',
      is_active: true
    });
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name || !form.name.trim()) {
      setSnackbar({ open: true, message: 'Benefit name is required', severity: 'error' });
      return;
    }

    if (!form.code || !form.code.trim()) {
      setSnackbar({ open: true, message: 'Benefit code is required', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        category: form.category,
        is_taxable: form.is_taxable,
        is_employer_paid: form.is_employer_paid,
        is_employee_paid: form.is_employee_paid,
        default_amount: form.default_amount || null,
        description: form.description || null,
        is_active: form.is_active
      };

      if (editId) {
        await updateBenefitType(editId, payload);
        setSnackbar({ open: true, message: 'Benefit type updated successfully', severity: 'success' });
      } else {
        await createBenefitType(payload);
        setSnackbar({ open: true, message: 'Benefit type created successfully', severity: 'success' });
      }
      
      handleCloseModal();
      fetchData();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message || 
                          Object.values(err.response?.data || {}).flat().join(', ') ||
                          err.message || 
                          'Failed to save benefit type';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const rowsWithSerial = useMemo(() => {
    return benefitTypes.map((item, index) => ({
      ...item,
      sn: pagination.page * pagination.rowsPerPage + index + 1
    }));
  }, [benefitTypes, pagination.page, pagination.rowsPerPage]);

  const columns = useMemo(() => [
    {
      field: 'sn',
      headerName: t('salary:benefits.types.columns.sn'),
      width: 80,
      sortable: false,
      filterable: false
    },
    {
      field: 'name',
      headerName: t('salary:benefits.types.columns.name'),
      flex: 1,
      minWidth: 150,
      sortable: false
    },
    {
      field: 'code',
      headerName: t('salary:benefits.types.columns.code'),
      flex: 1,
      minWidth: 120,
      sortable: false
    },
    {
      field: 'category',
      headerName: t('salary:benefits.types.columns.category'),
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const cat = params.value || 'other';
        return cat.charAt(0).toUpperCase() + cat.slice(1);
      }
    },
    {
      field: 'is_taxable',
      headerName: t('salary:benefits.types.columns.taxable'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('employees:leaveTypes.yes') : t('employees:leaveTypes.no')}
          color={params.value ? 'warning' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'is_employer_paid',
      headerName: t('salary:benefits.types.columns.employerPaid'),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('employees:leaveTypes.yes') : t('employees:leaveTypes.no')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'is_employee_paid',
      headerName: t('salary:benefits.types.columns.employeePaid'),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('employees:leaveTypes.yes') : t('employees:leaveTypes.no')}
          color={params.value ? 'info' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'default_amount',
      headerName: t('salary:benefits.types.columns.defaultAmount'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        return parseFloat(params.value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    },
    {
      field: 'is_active',
      headerName: t('salary:benefits.types.columns.status'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('salary:common.active') : t('salary:common.inactive')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    }
  ], [t]);

  if (loading && benefitTypes.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          {t('salary:benefits.types.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            {t('common:buttons.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal()}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('salary:benefits.types.addType')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label={t('employees:search')}
                fullWidth
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by name or code..."
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label="Category"
                fullWidth
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">{t('salary:common.all')}</option>
                <option value="health">Health</option>
                <option value="insurance">Insurance</option>
                <option value="retirement">Retirement</option>
                <option value="other">Other</option>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label={t('employees:status')}
                fullWidth
                value={filters.is_active}
                onChange={(e) => handleFilterChange('is_active', e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">{t('salary:common.all')}</option>
                <option value="true">{t('salary:common.active')}</option>
                <option value="false">{t('salary:common.inactive')}</option>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520
          }}>
            <DataGrid
              rows={rowsWithSerial}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>

      {/* Add/Edit Benefit Type Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editId ? t('common:buttons.edit') + ' ' + t('salary:benefits.types.title') : t('salary:benefits.types.addType')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('salary:benefits.types.columns.name')}
                  value={form.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  required
                  placeholder="e.g., HMO, Pension"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('salary:benefits.types.columns.code')}
                  value={form.code}
                  onChange={(e) => handleFormChange('code', e.target.value.toUpperCase())}
                  required
                  placeholder="e.g., HMO, PENSION"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label={t('salary:benefits.types.columns.category')}
                  value={form.category}
                  onChange={(e) => handleFormChange('category', e.target.value)}
                  SelectProps={{ native: true }}
                  required
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="health">Health</option>
                  <option value="insurance">Insurance</option>
                  <option value="retirement">Retirement</option>
                  <option value="other">Other</option>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('salary:benefits.types.columns.defaultAmount')}
                  type="number"
                  value={form.default_amount}
                  onChange={(e) => handleFormChange('default_amount', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  label={t('salary:benefits.types.columns.taxable')}
                  value={form.is_taxable ? 'true' : 'false'}
                  onChange={(e) => handleFormChange('is_taxable', e.target.value === 'true')}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="false">{t('employees:leaveTypes.no')}</option>
                  <option value="true">{t('employees:leaveTypes.yes')}</option>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  label={t('salary:benefits.types.columns.employerPaid')}
                  value={form.is_employer_paid ? 'true' : 'false'}
                  onChange={(e) => handleFormChange('is_employer_paid', e.target.value === 'true')}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="false">{t('employees:leaveTypes.no')}</option>
                  <option value="true">{t('employees:leaveTypes.yes')}</option>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  label={t('salary:benefits.types.columns.employeePaid')}
                  value={form.is_employee_paid ? 'true' : 'false'}
                  onChange={(e) => handleFormChange('is_employee_paid', e.target.value === 'true')}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="false">{t('employees:leaveTypes.no')}</option>
                  <option value="true">{t('employees:leaveTypes.yes')}</option>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Description"
                  value={form.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  multiline
                  rows={3}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  label={t('employees:status')}
                  value={form.is_active ? 'true' : 'false'}
                  onChange={(e) => handleFormChange('is_active', e.target.value === 'true')}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="true">{t('salary:common.active')}</option>
                  <option value="false">{t('salary:common.inactive')}</option>
                </TextField>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={saving}>
            {t('common:buttons.cancel')}
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {saving ? <CircularProgress size={20} /> : (editId ? t('common:buttons.update') : t('common:buttons.create'))}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BenefitTypes;

